import { useMemo } from 'react';
import { filter, flatMap, groupBy, map, sumBy } from 'lodash';
import moment from 'moment-timezone';
import { Box, ChevronDownIcon, ChevronUpIcon, HStack, Popover, Pressable, ScrollView, VStack, useTheme } from 'native-base';
import { useTranslation } from 'react-i18next';
import { FlatList } from 'react-native';

import { InfoCircleIcon } from '@pimm/base';
import { useModalFocus } from '@app/hooks/modal-focus.hook';
import { Text } from '@pimm/base';
import {
  DATA_YELLOW,
  IN_TEMP_YELLOW,
  SERVER_TZ,
  UPTIME_YELLOW,
  dataColor,
  fmt1,
  fmtLastSeen,
  getBatteryLabel,
  itpColor,
  tempColor,
  uptimeColor,
} from '../_helper';
import { useEquipmentConfig } from '../context/equipment-config.context';
import { LinkIcon } from '../icons/link';
import type { BatteryLevel, HubMeta, SensorMeta, SensorRow, SortKey, TableMode } from '../types';
import { HardwareTypeIcon } from './hardware-type-icon';
import { SensorInfoCard } from './sensor-info-card';
import type { EnableDisableSensor } from './sensor-setting-confirmation';
import { SensorSettingConfirmation } from './sensor-setting-confirmation';

type TFn = (key: string) => string;
const t$ = (t: TFn, key: string) => t(`store_equipment:${key}`);

const ROW_BORDER = { borderBottomWidth: 1, borderColor: 'gray.100' } as const;
const ACTIONS_W = 160;
const MINI_BAR_W = 32;
const MINI_BAR_H = 3;
const NAME_COL_MIN_W = 260;
const INFO_POPOVER_W = 280;

/* ── Group order ─────────────────────────────────────────────────────────── */

const GROUP_ORDER = ['Gateways', 'Walk-ins', 'Coolers', 'Freezers'];
const OTHER_GROUP = 'Other';
const groupRank = (g: string) => {
  const i = GROUP_ORDER.indexOf(g);
  return i === -1 ? GROUP_ORDER.length : i;
};
const GROUP_KEY: Record<string, string> = {
  Gateways: 'gateways',
  'Walk-ins': 'walk_ins',
  Coolers: 'coolers_group',
  Freezers: 'freezers',
  Other: 'other',
};
const groupLabel = (t: TFn, group: string) => (GROUP_KEY[group] ? t$(t, GROUP_KEY[group]) : group);

/* ── Sub-components ──────────────────────────────────────────────────────── */

const MiniBar = ({ pct, color }: { pct: number; color: string }) => (
  <Box bg="gray.100" rounded="sm" overflow="hidden" mt={0.5} style={{ width: MINI_BAR_W, height: MINI_BAR_H }}>
    <Box rounded="sm" style={{ width: `${Math.min(100, pct)}%`, height: '100%', backgroundColor: color }} />
  </Box>
);

type ConnectivityStatus = 'online' | 'offline' | 'disabled' | 'unknown';

const Pill = ({ bg, text, label, border }: { bg: string; text: string; label: string; border?: string }) => (
  <Box bg={bg} rounded="md" px={3} py={1} borderWidth={border ? 1 : 0} borderColor={border}>
    <Text size="sm" fontWeight={600} color={text} numberOfLines={1}>
      {label}
    </Text>
  </Box>
);

const CONN_PILL: Record<ConnectivityStatus, { bg: string; text: string; key: string; border?: string }> = {
  online: { bg: 'gray.100', text: 'gray.600', key: 'online' },
  offline: { bg: 'error.100', text: 'error.700', key: 'offline' },
  disabled: { bg: 'transparent', text: 'gray.500', key: 'disabled', border: 'gray.400' },
  unknown: { bg: 'gray.50', text: 'gray.400', key: 'no_data' },
};
const connLabel = (t: TFn, status: ConnectivityStatus) => t$(t, CONN_PILL[status].key);

const ConnectivityBadge = ({ status, t }: { status: ConnectivityStatus; t: TFn }) => {
  const s = CONN_PILL[status];
  return <Pill bg={s.bg} text={s.text} border={s.border} label={connLabel(t, status)} />;
};

const BATTERY_STYLES: Record<BatteryLevel, { bg: string; text: string }> = {
  ok: { bg: 'gray.100', text: 'gray.600' },
  info: { bg: 'amber.100', text: 'amber.800' },
  warning: { bg: 'orange.100', text: 'orange.700' },
  alarm: { bg: 'error.100', text: 'error.700' },
  dead: { bg: 'gray.900', text: 'white' },
};
const batteryLabel = (t: TFn, level: BatteryLevel | undefined, model?: string) => {
  if (level === undefined) return '';
  return t(getBatteryLabel(level, model));
};

const BatteryBadge = ({ level, model, t }: { level: BatteryLevel | undefined; model?: string; t: TFn }) => {
  if (level === undefined) return null;
  const s = BATTERY_STYLES[level];
  return <Pill bg={s.bg} text={s.text} label={batteryLabel(t, level, model)} />;
};

const sensorTypeLabel = (t: TFn, sensorType?: 'temp' | 'door') => t$(t, sensorType === 'door' ? 'door_sensor' : 'temperature_sensor');

const LinkedChip = ({ name }: { name: string }) => {
  const { colors } = useTheme();
  return (
    <Box bg="gray.100" rounded="sm" px={1.5} py={0.5} flexShrink={0}>
      <HStack space={1} alignItems="center">
        <LinkIcon size={4} color={colors.gray[500]} />
        <Text size="xs" fontWeight={700} color="gray.600" numberOfLines={1}>
          {name}
        </Text>
      </HStack>
    </Box>
  );
};

const NameCell = ({ s, meta }: { s: SensorRow; meta: SensorMeta | undefined }) => (
  <HStack space={1.5} alignItems="center" flex={1} overflow="hidden">
    <HardwareTypeIcon type={meta?.sensorType ?? 'temp'} size="sm" />
    <Text size="md" fontWeight={500} color="gray.900" numberOfLines={1} flexShrink={1}>
      {s.label}
    </Text>
    {meta?.linkedTsu ? <LinkedChip name={meta.linkedTsu} /> : null}
  </HStack>
);

const PctCell = ({ value, colorFn }: { value: number; colorFn: (v: number) => string }) => {
  const color = colorFn(value);
  return (
    <VStack alignItems="flex-start">
      <Text size="md" fontWeight={600} style={{ color }}>
        {fmt1(value)}%
      </Text>
      <MiniBar pct={value} color={color} />
    </VStack>
  );
};

/* ── Action buttons ──────────────────────────────────────────────────────── */

const ACTION_SLOT = 36;

const InfoTriggerBtn = (triggerProps: Record<string, unknown>) => {
  const { colors } = useTheme();
  return (
    <Pressable
      {...triggerProps}
      w={ACTION_SLOT}
      h={ACTION_SLOT}
      alignItems="center"
      justifyContent="center"
      rounded="md"
      _hover={{ bg: 'gray.100' }}
    >
      <InfoCircleIcon size="md" color={colors.gray[500]} />
    </Pressable>
  );
};

const RowActionBtn = ({ label, danger, onPress }: { label: string; danger?: boolean; onPress: () => void }) => (
  <Pressable onPress={onPress} rounded="md" _hover={{ opacity: 0.8 }}>
    <Box rounded="md" px={2.5} py={1} shadow={1} bg={danger ? 'error.600' : 'success.600'}>
      <Text size="sm" fontWeight={600} color="white" numberOfLines={1}>
        {label}
      </Text>
    </Box>
  </Pressable>
);

/* ── Column definitions ──────────────────────────────────────────────────── */

export interface SensorColumnDef {
  id: string;
  label: string;
  width?: number;
  flex?: number;
  minWidth?: number;
  maxWidth?: number;
  alwaysVisible?: boolean;
  actionOnly?: boolean;
  exportOnly?: boolean;
  modes: TableMode[];
  sortFn?: (a: SensorRow, b: SensorRow, ma?: SensorMeta, mb?: SensorMeta) => number;
  renderCell: (s: SensorRow, meta: SensorMeta | undefined) => React.ReactNode;
  renderText: (s: SensorRow, meta: SensorMeta | undefined) => string;
}

const BATTERY_SEVERITY: Record<BatteryLevel, number> = { ok: 0, info: 1, warning: 2, alarm: 3, dead: 4 };

const diagLeadColumns = (t: TFn): SensorColumnDef[] => [
  {
    id: 'sensor',
    label: t$(t, 'equipment_name'),
    flex: 1,
    minWidth: 260,
    maxWidth: 420,
    alwaysVisible: true,
    modes: ['diagnostics'],
    sortFn: (a, b) => a.label.localeCompare(b.label),
    renderCell: (s, meta) => <NameCell s={s} meta={meta} />,
    renderText: s => s.label,
  },
  {
    id: 'hardwareType',
    label: t$(t, 'hardware_type'),
    width: 160,
    exportOnly: true,
    modes: ['diagnostics'],
    sortFn: (a, b, ma, mb) => (ma?.sensorType ?? '').localeCompare(mb?.sensorType ?? ''),
    renderCell: (s, meta) => (
      <Text size="md" color="gray.700">
        {sensorTypeLabel(t, meta?.sensorType)}
      </Text>
    ),
    renderText: (s, meta) => sensorTypeLabel(t, meta?.sensorType),
  },
  {
    id: 'model',
    label: t$(t, 'model'),
    width: 150,
    modes: ['diagnostics'],
    renderCell: (s, meta) => (
      <Text size="md" color="gray.700">
        {meta?.model ?? '—'}
      </Text>
    ),
    renderText: (s, meta) => meta?.model ?? '',
  },
  {
    id: 'serial',
    label: t$(t, 'serial'),
    width: 150,
    modes: ['diagnostics'],
    renderCell: (s, meta) => (
      <Text size="md" color="gray.500">
        {meta?.serial ?? '—'}
      </Text>
    ),
    renderText: (s, meta) => meta?.serial ?? '',
  },
];

const buildDiagStatusColumns = (t: TFn): SensorColumnDef[] => [
  ...diagLeadColumns(t),
  {
    id: 'status',
    label: t$(t, 'status'),
    width: 100,
    alwaysVisible: true,
    modes: ['diagnostics'],
    sortFn: (a, b, ma, mb) => {
      const ord = (m?: SensorMeta) => (!m?.enabled ? 2 : !m?.isOnline ? 1 : 0);
      return ord(ma) - ord(mb);
    },
    renderCell: (s, meta) => {
      const status: ConnectivityStatus = !meta?.enabled ? 'disabled' : !meta?.isOnline ? 'offline' : 'online';
      return <ConnectivityBadge status={status} t={t} />;
    },
    renderText: (s, meta) => connLabel(t, !meta?.enabled ? 'disabled' : !meta?.isOnline ? 'offline' : 'online'),
  },
  {
    id: 'battery',
    label: t$(t, 'battery'),
    width: 150,
    alwaysVisible: true,
    modes: ['diagnostics'],
    sortFn: (a, b, ma, mb) => (BATTERY_SEVERITY[mb?.batteryLevel ?? 'ok'] ?? 0) - (BATTERY_SEVERITY[ma?.batteryLevel ?? 'ok'] ?? 0),
    renderCell: (s, meta) => <BatteryBadge level={meta?.batteryLevel} model={meta?.model} t={t} />,
    renderText: (s, meta) => batteryLabel(t, meta?.batteryLevel, meta?.model),
  },
  {
    id: 'lastSeen',
    label: t$(t, 'last_update'),
    width: 135,
    modes: ['diagnostics'],
    sortFn: (a, b, ma, mb) => {
      const ts = (m?: SensorMeta) => (m?.lastSeen ? moment.tz(m.lastSeen, SERVER_TZ).valueOf() : 0);
      return ts(mb) - ts(ma);
    },
    renderCell: (s, meta) => (
      <Text size="md" color="gray.600">
        {fmtLastSeen(meta?.lastSeen)}
      </Text>
    ),
    renderText: (s, meta) => fmtLastSeen(meta?.lastSeen),
  },
];

const buildDiagRollupColumns = (t: TFn): SensorColumnDef[] => [
  ...diagLeadColumns(t),
  {
    id: 'uptime',
    label: t$(t, 'uptime'),
    width: 120,
    alwaysVisible: true,
    modes: ['diagnostics'],
    sortFn: (a, b) => b.uptimePct - a.uptimePct,
    renderCell: s => <PctCell value={s.uptimePct} colorFn={uptimeColor} />,
    renderText: s => `${fmt1(s.uptimePct)}%`,
  },
  {
    id: 'upload',
    label: t$(t, 'data_upload'),
    width: 150,
    modes: ['diagnostics'],
    sortFn: (a, b) => b.uploadPct - a.uploadPct,
    renderCell: s => <PctCell value={s.uploadPct} colorFn={dataColor} />,
    renderText: s => `${fmt1(s.uploadPct)}%`,
  },
  {
    id: 'offline',
    label: t$(t, 'offline_time'),
    width: 130,
    modes: ['diagnostics'],
    sortFn: (a, b) => b.offlineMinutes - a.offlineMinutes,
    renderCell: s => (
      <Text size="md" color="gray.600">
        {s.offlineDuration ?? '—'}
      </Text>
    ),
    renderText: s => s.offlineDuration ?? '',
  },
  {
    id: 'oos',
    label: t$(t, 'oos_time'),
    width: 105,
    modes: ['diagnostics'],
    renderCell: s => (
      <Text size="md" color="gray.600">
        {s.oosDuration ?? '—'}
      </Text>
    ),
    renderText: s => s.oosDuration ?? '',
  },
  {
    id: 'offEvts',
    label: t$(t, 'offline_events'),
    width: 150,
    modes: ['diagnostics'],
    sortFn: (a, b) => b.offlineEvents - a.offlineEvents,
    renderCell: s => (
      <Text size="md" fontWeight={600} color={s.offlineEvents > 0 ? 'error.600' : 'gray.400'}>
        {s.offlineEvents}
      </Text>
    ),
    renderText: s => String(s.offlineEvents),
  },
  {
    id: 'batteryEvts',
    label: t$(t, 'battery_events'),
    width: 150,
    modes: ['diagnostics'],
    sortFn: (a, b) => b.batteryEvents - a.batteryEvents,
    renderCell: s => (
      <Text size="md" fontWeight={600} color={s.batteryEvents > 0 ? 'amber.600' : 'gray.400'}>
        {s.batteryEvents}
      </Text>
    ),
    renderText: s => String(s.batteryEvents),
  },
];

const buildPerfColumns = (t: TFn): SensorColumnDef[] => [
  {
    id: 'sensor',
    label: t$(t, 'equipment_name'),
    flex: 1,
    minWidth: 260,
    maxWidth: 420,
    alwaysVisible: true,
    modes: ['performance'],
    sortFn: (a, b) => a.label.localeCompare(b.label),
    renderCell: (s, meta) => <NameCell s={s} meta={meta} />,
    renderText: s => s.label,
  },
  {
    id: 'hardwareType',
    label: t$(t, 'hardware_type'),
    width: 170,
    exportOnly: true,
    modes: ['performance'],
    sortFn: (a, b, ma, mb) => (ma?.sensorType ?? '').localeCompare(mb?.sensorType ?? ''),
    renderCell: (s, meta) => (
      <Text size="md" color="gray.700">
        {sensorTypeLabel(t, meta?.sensorType)}
      </Text>
    ),
    renderText: (s, meta) => sensorTypeLabel(t, meta?.sensorType),
  },
  {
    id: 'model',
    label: t$(t, 'model'),
    width: 150,
    modes: ['performance'],
    renderCell: (s, meta) => (
      <Text size="md" color="gray.700">
        {meta?.model ?? '—'}
      </Text>
    ),
    renderText: (s, meta) => meta?.model ?? '',
  },
  {
    id: 'serial',
    label: t$(t, 'serial'),
    width: 150,
    modes: ['performance'],
    renderCell: (s, meta) => (
      <Text size="md" color="gray.500">
        {meta?.serial ?? '—'}
      </Text>
    ),
    renderText: (s, meta) => meta?.serial ?? '',
  },
  {
    id: 'avgTemp',
    label: t$(t, 'avg_temp'),
    width: 115,
    modes: ['performance'],
    sortFn: (a, b, ma, mb) => {
      if (ma?.sensorType === 'door') return 1;
      if (mb?.sensorType === 'door') return -1;
      return (b.averageTemperature ?? -Infinity) - (a.averageTemperature ?? -Infinity);
    },
    renderCell: (s, meta) => {
      if (meta?.sensorType === 'door' || s.averageTemperature == null) return null;
      return (
        <Text size="md" fontWeight={600} style={{ color: tempColor(s.averageTemperature, meta?.thresholds) }}>
          {s.averageTemperature.toFixed(1)}°F
        </Text>
      );
    },
    renderText: (s, meta) => (meta?.sensorType === 'door' || s.averageTemperature == null ? '' : `${s.averageTemperature.toFixed(1)}°F`),
  },
  {
    id: 'inTemp',
    label: t$(t, 'in_temp_tab'),
    width: 110,
    modes: ['performance'],
    sortFn: (a, b, ma, mb) => {
      if (ma?.sensorType === 'door') return 1;
      if (mb?.sensorType === 'door') return -1;
      return (b.inTempPct ?? -Infinity) - (a.inTempPct ?? -Infinity);
    },
    renderCell: (s, meta) => {
      if (meta?.sensorType === 'door' || s.inTempPct == null) return null;
      return <PctCell value={s.inTempPct} colorFn={itpColor} />;
    },
    renderText: (s, meta) => (meta?.sensorType === 'door' || s.inTempPct == null ? '' : `${fmt1(s.inTempPct)}%`),
  },
  {
    id: 'tempEvts',
    label: t$(t, 'temperature_events'),
    width: 180,
    modes: ['performance'],
    sortFn: (a, b, ma, mb) => {
      if (ma?.sensorType === 'door') return 1;
      if (mb?.sensorType === 'door') return -1;
      return (b.temperatureEvents ?? 0) - (a.temperatureEvents ?? 0);
    },
    renderCell: (s, meta) => {
      if (meta?.sensorType === 'door' || s.temperatureEvents == null) return null;
      return (
        <Text size="md" fontWeight={600} color={s.temperatureEvents > 0 ? 'error.600' : 'gray.400'}>
          {s.temperatureEvents}
        </Text>
      );
    },
    renderText: (s, meta) => (meta?.sensorType === 'door' || s.temperatureEvents == null ? '' : String(s.temperatureEvents)),
  },
  {
    id: 'doorEvts',
    label: t$(t, 'door_events'),
    width: 135,
    modes: ['performance'],
    sortFn: (a, b) => b.doorEvents - a.doorEvents,
    renderCell: s => (
      <Text size="md" fontWeight={600} color={s.doorEvents > 0 ? 'error.600' : 'gray.400'}>
        {s.doorEvents}
      </Text>
    ),
    renderText: s => String(s.doorEvents),
  },
];

const ID_T: TFn = k => k;

export const getSensorColumns = (mode: TableMode, isStatusMode = false, t: TFn = ID_T): SensorColumnDef[] => {
  if (mode === 'performance') return buildPerfColumns(t);
  return isStatusMode ? buildDiagStatusColumns(t) : buildDiagRollupColumns(t);
};

export const defaultVisibleIds = (mode: TableMode, isStatusMode = false): string[] =>
  getSensorColumns(mode, isStatusMode)
    .filter(c => !c.actionOnly && !c.exportOnly)
    .map(c => c.id);

export const cycleSort = (current: SortKey[], colId: string): SortKey[] => {
  const existing = current.find(sk => sk.id === colId);
  if (!existing) return [{ id: colId, dir: 'asc' }];
  return [{ id: colId, dir: existing.dir === 'asc' ? 'desc' : 'asc' }];
};

/* ── FlatRow ─────────────────────────────────────────────────────────────── */

type FlatRow = { kind: 'sensor'; sensor: SensorRow; meta: SensorMeta | undefined } | { kind: 'gateway'; hub: HubMeta };

const getRowGroup = (row: FlatRow): string => (row.kind === 'gateway' ? 'Gateways' : row.sensor.displayGroup);

/* ── Column header ───────────────────────────────────────────────────────── */

const ColHeader = ({
  col,
  sortKey,
  onSort,
  align = 'center',
}: {
  col: SensorColumnDef;
  sortKey?: SortKey;
  onSort?: (id: string) => void;
  align?: 'flex-start' | 'center';
}) => {
  const isSortable = !!col.sortFn && !!onSort;
  const sizingStyle = {
    ...(col.width !== undefined ? { width: col.width, flexShrink: 0 } : {}),
    ...(col.flex !== undefined ? { flex: col.flex } : {}),
    ...(col.minWidth !== undefined ? { minWidth: col.minWidth } : {}),
    ...(col.maxWidth !== undefined ? { maxWidth: col.maxWidth } : {}),
    alignItems: align,
    justifyContent: 'center' as const,
  };

  const inner = (
    <HStack w="100%" space={1.5} alignItems="center" px={4} py={3} justifyContent={align === 'flex-start' ? 'flex-start' : 'center'}>
      {col.label && (
        <Text size="sm" fontWeight={600} color="gray.700" numberOfLines={1}>
          {col.label}
        </Text>
      )}
      {isSortable &&
        sortKey &&
        (sortKey.dir === 'asc' ? <ChevronUpIcon size={4} color="primary.700" /> : <ChevronDownIcon size={4} color="primary.700" />)}
    </HStack>
  );

  return isSortable ? (
    <Pressable style={sizingStyle} onPress={() => onSort!(col.id)} _hover={{ bg: 'gray.100' }}>
      {inner}
    </Pressable>
  ) : (
    <Box style={sizingStyle}>{inner}</Box>
  );
};

/* ── SensorTable ─────────────────────────────────────────────────────────── */

type ListItem =
  | { kind: 'col-header' }
  | { kind: 'group-header'; group: string }
  | { kind: 'gateway'; hub: HubMeta }
  | { kind: 'sensor'; sensor: SensorRow; meta: SensorMeta | undefined }
  | { kind: 'empty' };

type SensorTableProps = {
  sensors: SensorRow[];
  sensorMeta: Record<string, SensorMeta>;
  hubs: HubMeta[];
  mode: TableMode;
  isStatusMode: boolean;
  visibleColumnIds: string[];
  sortKeys: SortKey[];
  onSortChange: (keys: SortKey[]) => void;
  groupFilter: string[];
  quickFilter?: string;
  topBand?: React.ReactNode;
  siteId?: string;
  onToggleOutOfService?: (label: string, oos: boolean) => void;
};

export const SensorTable = ({
  sensors,
  sensorMeta,
  hubs,
  mode,
  isStatusMode,
  visibleColumnIds,
  sortKeys,
  onSortChange,
  groupFilter,
  quickFilter,
  topBand,
  siteId,
  onToggleOutOfService,
}: SensorTableProps) => {
  const { t } = useTranslation();
  const canEdit = useEquipmentConfig(ctx => ctx.canEditSensor);

  const confirmSensor = useModalFocus<EnableDisableSensor>();

  const allColumns = getSensorColumns(mode, isStatusMode, t);
  const visibleCols = allColumns.filter(c => !c.exportOnly && (c.alwaysVisible || visibleColumnIds.includes(c.id)));
  const showActions = isStatusMode;

  const minWidth = useMemo(() => {
    const fixedSum = visibleCols.filter(c => c.width != null).reduce((sum, c) => sum + c.width!, 0);
    return NAME_COL_MIN_W + fixedSum + (showActions ? ACTIONS_W : 0);
  }, [visibleCols, showActions]);

  const sections = useMemo((): { group: string; rows: FlatRow[] }[] => {
    const all: FlatRow[] = [
      ...map(sensors, s => ({ kind: 'sensor' as const, sensor: s, meta: sensorMeta[s.label] })),
      ...(mode === 'diagnostics' && isStatusMode ? map(hubs, h => ({ kind: 'gateway' as const, hub: h })) : []),
    ];

    const matchesQuick = (row: FlatRow): boolean => {
      const q = quickFilter;
      if (!q || q === 'all') return true;

      if (mode === 'diagnostics' && isStatusMode) {
        if (row.kind === 'gateway') return true; // quick filters are sensor-only — gateways always shown
        const m = row.meta;
        if (q === 'online') return !!(m?.enabled && m?.isOnline);
        if (q === 'offline') return !(m?.enabled && m?.isOnline);
        if (q === 'battery') return !!(m?.batteryLevel && m.batteryLevel !== 'ok');
        return true;
      }

      if (row.kind !== 'sensor') return false;
      const s = row.sensor;
      if (mode === 'diagnostics') {
        if (q === 'lowUptime') return s.uptimePct < UPTIME_YELLOW;
        if (q === 'lowData') return s.uploadPct < DATA_YELLOW;
        if (q === 'offlineEvents') return s.offlineEvents > 0;
        if (q === 'oos') return !!s.oosDuration;
        if (q === 'batteryEvents') return s.batteryEvents > 0;
        return true;
      }
      if (q === 'lowInTemp') return s.inTempPct != null && s.inTempPct < IN_TEMP_YELLOW;
      if (q === 'tempEvents') return (s.temperatureEvents ?? 0) > 0;
      if (q === 'doorEvents') return s.doorEvents > 0;
      return true;
    };

    const filtered = filter(all, row => (groupFilter.length === 0 || groupFilter.includes(getRowGroup(row))) && matchesQuick(row));
    const grouped = groupBy(filtered, row => getRowGroup(row) || OTHER_GROUP);

    const sortRows = (rows: FlatRow[]) =>
      [...rows].sort((a, b) => {
        for (const sk of sortKeys) {
          if (a.kind === 'gateway' || b.kind === 'gateway') continue;
          const col = allColumns.find(c => c.id === sk.id);
          if (!col?.sortFn) continue;
          const cmp = col.sortFn(a.sensor, b.sensor, a.meta, b.meta);
          if (cmp !== 0) return sk.dir === 'asc' ? cmp : -cmp;
        }
        return 0;
      });

    return (Object.keys(grouped) as string[])
      .sort((x, y) => groupRank(x) - groupRank(y) || x.localeCompare(y))
      .map(group => ({ group, rows: sortRows(grouped[group]) }));
  }, [sensors, sensorMeta, hubs, mode, isStatusMode, sortKeys, allColumns, groupFilter, quickFilter]);

  const handleSort = (colId: string) => onSortChange(cycleSort(sortKeys, colId));

  const flatItems = useMemo<ListItem[]>(() => {
    const header: ListItem = { kind: 'col-header' };
    if (sumBy(sections, s => s.rows.length) === 0) return [header, { kind: 'empty' }];
    return [
      header,
      ...flatMap(sections, section => [
        { kind: 'group-header' as const, group: section.group },
        ...section.rows.map<ListItem>(row =>
          row.kind === 'gateway' ? { kind: 'gateway', hub: row.hub } : { kind: 'sensor', sensor: row.sensor, meta: row.meta },
        ),
      ]),
    ];
  }, [sections]);

  const cellSizing = (col: SensorColumnDef) => ({
    ...(col.width !== undefined ? { width: col.width, flexShrink: 0 } : {}),
    ...(col.flex !== undefined ? { flex: col.flex } : {}),
    ...(col.minWidth !== undefined ? { minWidth: col.minWidth } : {}),
    ...(col.maxWidth !== undefined ? { maxWidth: col.maxWidth } : {}),
    paddingLeft: 16,
    paddingRight: 16,
    overflow: 'hidden' as const,
    alignItems: 'flex-start' as const,
    justifyContent: 'center' as const,
  });

  const renderDataCell = (col: SensorColumnDef, s: SensorRow, meta: SensorMeta | undefined, dim = false) => (
    <Box key={col.id} style={cellSizing(col)} opacity={dim && col.id !== 'status' ? 0.4 : 1}>
      {col.renderCell(s, meta)}
    </Box>
  );

  const renderGatewayCell = (col: SensorColumnDef, hub: HubMeta) => {
    let content: React.ReactNode = null;
    if (col.id === 'sensor') {
      content = (
        <HStack space={1.5} alignItems="center">
          <HardwareTypeIcon type="gateway" size="sm" />
          <Text size="md" fontWeight={500} color="gray.900" numberOfLines={1}>
            {hub.label}
          </Text>
          {hub.isPrimary && (
            <Box bg="blue.50" rounded="full" px={2} py={1}>
              <Text size="xs" fontWeight={700} color="blue.700" textTransform="uppercase">
                {t$(t, 'primary')}
              </Text>
            </Box>
          )}
        </HStack>
      );
    } else if (col.id === 'hardwareType') {
      content = (
        <Text size="md" color="gray.700">
          {t$(t, 'gateway')}
        </Text>
      );
    } else if (col.id === 'model') {
      content = (
        <Text size="md" color="gray.700">
          {hub.model ?? hub.type ?? '—'}
        </Text>
      );
    } else if (col.id === 'serial') {
      content = (
        <Text size="md" color="gray.500">
          {hub.serial}
        </Text>
      );
    } else if (col.id === 'status') {
      const status: ConnectivityStatus = hub.online === true ? 'online' : hub.online === false ? 'offline' : 'unknown';
      content = <ConnectivityBadge status={status} t={t} />;
    } else if (col.id === 'lastSeen') {
      content = (
        <Text size="md" color="gray.600">
          {fmtLastSeen(hub.lastReportedTime)}
        </Text>
      );
    }
    return (
      <Box key={col.id} style={cellSizing(col)}>
        {content}
      </Box>
    );
  };

  const openToggleSensor = (s: SensorRow, meta: SensorMeta | undefined, disabled: boolean) => () =>
    confirmSensor.setOpen({
      instanceId: meta?.instanceId,
      equipmentId: meta?.equipmentId,
      isEnabled: !disabled,
      isDoor: meta?.sensorType === 'door',
      sensorName: s.label,
    });

  const renderItem = ({ item }: { item: ListItem }) => {
    if (item.kind === 'col-header') {
      return (
        <Box borderLeftWidth={3} borderLeftColor="transparent">
          <HStack px={0} py={0} bg="coolGray.50" borderBottomWidth={1} borderColor="gray.200" alignItems="stretch">
            {visibleCols.map(col => (
              <ColHeader
                key={col.id}
                col={col}
                sortKey={sortKeys.find(sk => sk.id === col.id)}
                onSort={col.sortFn ? handleSort : undefined}
                align="flex-start"
              />
            ))}
            {showActions && <Box w={ACTIONS_W} flexShrink={0} />}
          </HStack>
        </Box>
      );
    }

    if (item.kind === 'group-header') {
      return (
        <Box px={4} py={2} bg="gray.100" borderBottomWidth={1} borderColor="gray.200">
          <Text size="sm" fontWeight={600} color="gray.700">
            {groupLabel(t, item.group)}
          </Text>
        </Box>
      );
    }

    if (item.kind === 'gateway') {
      const { hub } = item;
      return (
        <Box borderLeftWidth={3} borderLeftColor="transparent">
          <HStack px={0} py={3} bg="white" {...ROW_BORDER} alignItems="center">
            {visibleCols.map(col => renderGatewayCell(col, hub))}
            {showActions && (
              <Box w={ACTIONS_W} flexShrink={0}>
                <HStack alignItems="center" pl={1}>
                  <Popover placement="left top" trigger={InfoTriggerBtn}>
                    <Popover.Content w={INFO_POPOVER_W} shadow="4" rounded="xl" borderWidth={1} borderColor="gray.200">
                      <Popover.Body p={0} bg="white" borderWidth={0}>
                        <SensorInfoCard kind="hub" name={hub.label} meta={hub} />
                      </Popover.Body>
                    </Popover.Content>
                  </Popover>
                </HStack>
              </Box>
            )}
          </HStack>
        </Box>
      );
    }

    if (item.kind === 'sensor') {
      const { sensor: s, meta: rowMeta } = item;
      const isDisabled = !rowMeta?.enabled;
      const isOffline = !!rowMeta?.enabled && !rowMeta?.isOnline;
      return (
        <Box borderLeftWidth={3} borderLeftColor="transparent">
          <HStack px={0} py={3} bg="white" {...ROW_BORDER} alignItems="center">
            {visibleCols.map(col => renderDataCell(col, s, rowMeta, isDisabled))}
            {showActions && (
              <Box w={ACTIONS_W} flexShrink={0}>
                <HStack space={2} alignItems="center" pl={1}>
                  {rowMeta && (
                    <Popover placement="left top" trigger={InfoTriggerBtn}>
                      <Popover.Content w={INFO_POPOVER_W} shadow="4" rounded="xl" borderWidth={1} borderColor="gray.200">
                        <Popover.Body p={0} bg="white" borderWidth={0}>
                          <SensorInfoCard
                            kind="sensor"
                            name={s.label}
                            meta={rowMeta}
                            canEdit={canEdit}
                            onRequestToggle={confirmSensor.setOpen}
                          />
                        </Popover.Body>
                      </Popover.Content>
                    </Popover>
                  )}
                  {canEdit && (isDisabled || isOffline) && (
                    <RowActionBtn
                      label={t$(t, isDisabled ? 'enable' : 'disable')}
                      danger={!isDisabled}
                      onPress={openToggleSensor(s, rowMeta, isDisabled)}
                    />
                  )}
                </HStack>
              </Box>
            )}
          </HStack>
        </Box>
      );
    }

    return (
      <Box p={8} alignItems="center">
        <Text size="md" color="gray.400">
          {t$(t, 'no_sensor_data_available')}
        </Text>
      </Box>
    );
  };

  return (
    <>
      <VStack flex={1} bg="white" rounded="lg" borderWidth={1} borderColor="gray.200" overflow="hidden">
        {topBand}
        <ScrollView horizontal flex={1} contentContainerStyle={{ minWidth, flex: 1 }}>
          <FlatList
            data={flatItems}
            keyExtractor={(item, index) => `${item.kind}-${index}`}
            renderItem={renderItem}
            stickyHeaderIndices={[0]}
            showsVerticalScrollIndicator
          />
        </ScrollView>
      </VStack>
      <SensorSettingConfirmation
        isOpen={confirmSensor.isOpen}
        sensor={confirmSensor.payload}
        siteId={siteId}
        onClose={confirmSensor.setHide}
        onSuccess={onToggleOutOfService}
      />
    </>
  );
};
